import dayjs from 'dayjs';
import * as R from 'ramda';
import Select from 'react-select';
// mui
import Input from '@mui/material/Input';
import FormGroup from '@mui/material/FormGroup';
// constants
import * as C from '../../../../constants';
// utilities
import * as U from '../../../../utilities';
// forms
import DatePickerMui from '../../../date-picker';
// components
import FieldSelect from '../field-select';
import { withScrollToViewOnOpen } from '../field-list/hocs';
// component report-format-panel
import { FilterDate, MultiSelectWrapper } from '../../../report-format-panel/ui';
import { reportBooleanValues } from '../../../report-format-panel/settings/filter-settings';
// ui
import { Box } from '../../../../ui';
//////////////////////////////////////////////////

const enterValuePlaceholder = U.getTranslation('labels.enterValue', 'Enter a value');

export const FilterSelect = withScrollToViewOnOpen(({
  zI,
  id,
  name,
  value,
  theme,
  margin,
  action,
  options,
  groupId,
  dataType,
  filterIndex,
  placeholder,
  withoutOrGroup,
  filterFieldWidth,
}) => (
  <Box
    zIndex={zI}
    margin={margin}
    width={R.or(filterFieldWidth, 232)}
  >
    <FieldSelect
      id={id}
      name={name}
      theme={theme}
      value={value}
      action={action}
      options={options}
      groupId={groupId}
      dataType={dataType}
      fieldIndex={filterIndex}
      placeholder={placeholder}
      withoutOrGroup={withoutOrGroup}
    />
  </Box>
));

const BooleanComponent = props => {
  const {
    id,
    groupId,
    filterIndex,
    booleanValue,
    handleChangeSelect,
  } = props;

  let value = R.or(booleanValue, '');

  if (U.isTrue(booleanValue)) {
    value = U.getTranslation('labels.yes', 'Yes');
  }

  if (U.isFalse(booleanValue)) {
    value = U.getTranslation('labels.no', 'No');
  }

  return (
    <FilterSelect
      id={id}
      value={value}
      groupId={groupId}
      filterIndex={filterIndex}
      options={reportBooleanValues()}
      action={values => handleChangeSelect({
        values,
        groupId,
        filterIndex,
        filterProp: 'booleanValue',
      })}
    />
  );
};

const getInputNumber = event => {
  const value = U.getEventTargetValue(event);

  if (U.isNilOrEmpty(value)) return null;

  return U.toNumber(value);
};

const NumberComponent = props => {
  const {
    groupId,
    operation,
    filterIndex,
    numberValue,
    numberRange,
    handleChangeInput,
    handleChangeRange,
  } = props;

  if (R.equals(operation, 'range')) {
    return (
      <FormGroup direction='row'>
        {
          ['from', 'to'].map((prefix, i) => (
            <Input
              key={i}
              min={0}
              m='0 10px'
              width={106}
              type='number'
              sx={{ marginLeft: '5px' }}
              placeholder={enterValuePlaceholder}
              value={R.pathOr('', [prefix], numberRange)}
              onChange={event => handleChangeRange({
                groupId,
                filterIndex,
                rangeType: prefix,
                filterProp: 'numberRange',
                value: getInputNumber(event),
              })}
            />
          ))
        }
      </FormGroup>
    );
  }

  return (
    <FormGroup>
      <Input
        min={0}
        m='0 10px'
        width={232}
        type='number'
        value={numberValue}
        sx={{ marginLeft: '5px' }}
        placeholder={enterValuePlaceholder}
        onChange={event => (
          handleChangeInput({
            groupId, filterIndex, filterProp: 'numberValue', value: getInputNumber(event),
          })
        )}
      />
    </FormGroup>
  );
};

const StringComponent = props => {
  const {
    groupId,
    operation,
    filterIndex,
    stringValue,
    stringRange,
    handleChangeInput,
    handleChangeRange,
  } = props;

  if (R.equals(operation, 'range')) {
    return (
      <FormGroup sx={{ width: '240px', flexDirection: 'row', justifyContent: 'space-around' }}>
        {
          ['from', 'to'].map((prefix, i) => (
            <Input
              key={i}
              width={106}
              type={C.FIELD_TYPE_TEXT}
              placeholder={enterValuePlaceholder}
              value={R.pathOr('', [prefix], stringRange)}
              onChange={event => handleChangeRange({
                groupId,
                filterIndex,
                rangeType: prefix,
                filterProp: 'stringRange',
                value: U.getEventTargetValue(event),
              })}
            />
          ))
        }
      </FormGroup>
    );
  }

  return (
    <FormGroup direction='row'>
      <Input
        min={0}
        m='0 10px'
        width={232}
        value={stringValue}
        type={C.FIELD_TYPE_TEXT}
        sx={{ marginLeft: '5px' }}
        placeholder={enterValuePlaceholder}
        onChange={event => (
          handleChangeInput({
            groupId,
            filterIndex,
            filterProp: 'stringValue',
            value: U.getEventTargetValue(event),
          })
        )}
      />
    </FormGroup>
  );
};

const DateComponent = props => {
  const {
    name,
    groupId,
    filterIndex,
    dateRelative,
    handleChangeDate,
  } = props;

  if (U.isTrue(dateRelative)) {
    const lastNextValues = R.pick(['last', 'next'], props);

    return (
      <FilterDate>
        {
          ['last', 'next'].map((prefix, i) => (
            <Input
              key={i}
              m='0 10px'
              width={106}
              name={name}
              type='number'
              value={R.pathOr('', [prefix], lastNextValues)}
              placeholder={U.getTranslation(`labels.${prefix}`, prefix)}
              onChange={event => handleChangeDate({
                groupId,
                filterIndex,
                rangeType: prefix,
                filterProp: 'dateRelative',
                value: U.getEventTargetValue(event),
              })}
            />
          ))
        }
      </FilterDate>
    );
  }

  const fromToValues = R.pick(['from', 'to'], props);

  return (
    <FilterDate>
      {
        ['from', 'to'].map((prefix, i) => {
          let maxDate;
          let minDate;

          const isFrom = R.and(R.equals(prefix, 'from'), U.isNotNilAndNotEmpty(fromToValues.to));

          if (isFrom) {
            maxDate = dayjs(fromToValues.to).startOf('day');
          }

          if (R.equals(prefix, 'to')) {
            minDate = dayjs(fromToValues.from).endOf('day');
          }

          return (
            <DatePickerMui
              key={i}
              m='0 10px'
              width={150}
              name={name}
              maxDate={maxDate}
              minDate={minDate}
              value={R.pathOr('', [prefix], fromToValues)}
              placeholder={
                `${U.getTranslation('labels.selectDate', 'Select Date')} ${
                  U.getTranslation(`labels.${prefix}`, prefix)}`
              }
              onChange={(name, date) => {
                handleChangeDate({
                  groupId,
                  filterIndex,
                  value: date,
                  rangeType: prefix,
                  filterProp: C.FIELD_TYPE_DATE_RANGE,
                });
              }}
            />
          );
        })
      }
    </FilterDate>
  );
};

const getMultiSelectValue = (selectMultipleValue, stringValue) => {
  if (U.isNotNilAndNotEmpty(selectMultipleValue)) {
    return R.map(R.prop(C.FIELD_VALUE), selectMultipleValue);
  } if (U.isNotNilAndNotEmpty(stringValue)) {
    return R.split(',', stringValue);
  }

  return [];
};

const MultipleSelectComponent = withScrollToViewOnOpen(props => {
  const {
    id,
    groupId,
    handleOpen,
    filterIndex,
    stringValue,
    propertyName,
    selectMultipleValue,
    handleChangeMultiSelect,
  } = props;

  let searchOptions;

  if (U.isNilOrEmpty(R.path(['filterBy'], props))) {
    searchOptions = R.find(R.propEq(propertyName, C.FIELD_VALUE))(R.path(['availableFields'], props));
  } else {
    searchOptions = R.find(R.propEq(propertyName, C.FIELD_NAME))(R.path(['filterBy'], props));
  }

  const { options, placeholder } = searchOptions;

  let optionsToUse = options;

  if (U.isFunction(options)) optionsToUse = options();

  let placeholderValue = '';

  if (U.isNotNilAndNotEmpty(placeholder)) {
    placeholderValue = U.getTranslation(placeholder.key, placeholder.text);
  }

  return (
    <FormGroup>
      <MultiSelectWrapper m='5px 10px' width={232}>
        <Select
          id={id}
          multi={true}
          clearable={true}
          onOpen={handleOpen}
          options={optionsToUse}
          placeholder={placeholderValue}
          onChange={options => handleChangeMultiSelect({ options, groupId, filterIndex })}
          value={{
            [C.FIELD_LABEL]: U.getTranslation(
              `labels.${getMultiSelectValue(selectMultipleValue, stringValue)}`,
              getMultiSelectValue(selectMultipleValue, stringValue),
            ),
          }}
        />
      </MultiSelectWrapper>
    </FormGroup>
  );
});

const SingleSelectComponent = withScrollToViewOnOpen(props => {
  const {
    id,
    groupId,
    handleOpen,
    filterIndex,
    stringValue,
    propertyName,
    handleChangeSelect,
  } = props;

  const value = R.or(U.getTranslation(`labels.${stringValue}`, stringValue), '');
  const reportFilterParams = R.pathOr([], ['reportFilterParams'], props);

  const findCustomFieldOptions = (propertyName, reportFilterParams) => {
    const isStringSelect = R.propSatisfies(R.includes('enum'), 'type');
    const matchesPropertyName = R.propEq(propertyName, 'name');

    const selectedItem = R.compose(
      R.find(matchesPropertyName),
      R.filter(isStringSelect),
    )(reportFilterParams);

    return R.pathOr(null, ['customFieldOptions'], selectedItem);
  };

  let searchOptions;

  if (U.isNilOrEmpty(R.path(['filterBy'], props))) {
    searchOptions = findCustomFieldOptions(propertyName, reportFilterParams);
  } else {
    searchOptions = R.find(R.propEq(propertyName, C.FIELD_NAME))(R.path(['filterBy'], props));
  }

  return (
    <Box width={232}>
      <FilterSelect
        id={id}
        value={value}
        groupId={groupId}
        onOpen={handleOpen}
        options={searchOptions}
        filterIndex={filterIndex}
        action={values => handleChangeSelect({
          values,
          groupId,
          filterIndex,
          filterProp: 'stringValue',
        })}
      />
    </Box>
  );
});

export const renderField = props => {
  const dataType = R.path(['dataType'], props);
  const propertyName = R.path(['propertyName'], props);
  const availableFields = R.pathOr([], ['availableFields'], props);

  const getDataType = (propertyName, fields) => {
    const field = R.find(obj => R.equals(R.path([C.FIELD_NAME], obj), propertyName), fields);

    if (U.isNotNilAndNotEmpty(dataType)) return dataType;

    if (U.isNotNilAndNotEmpty(field)) return R.path([C.FIELD_TYPE], field);

    return 'string';
  };

  const foundedDataType = getDataType(propertyName, availableFields);

  const dataTypeCompMap = {
    date: DateComponent,
    number: NumberComponent,
    string: StringComponent,
    boolean: BooleanComponent,
    'enum': SingleSelectComponent,
    'string:array': StringComponent,
    selectMultiple: MultipleSelectComponent,
  };

  const FieldComponent = dataTypeCompMap[foundedDataType];

  if (U.isNilOrEmpty(FieldComponent)) return null;

  return <FieldComponent {...props} />;
};
